import { FC, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import Routes from '../../core/Routes';
import Page from '../../components/Page/Page';
import { GlobalContext } from '../../GlobalContext';
import Loader from '../../components/Loader/Loader';
import ContentWatchComponent from './ContentWatch/ContentWatch';
import Checkbox from '../../components/Checkbox/Checkbox';
import useContentWatch from '../../core/apiHooks/useContentWatch';
import useSuggestedTags from '../../core/apiHooks/useSuggestedTags';
import { ContentWatch, iContentWatch, SubscriptionType } from '../../core/models/ContentWatch';
import useNotification from '../../core/hooks/useNotification';

import './Alerts.styles.scss';
import { NEWS_FEATURE_FLAG } from '../../components/Page/PlatformHeader';
import { usePlatformFeatureFlag } from '@datassential/platform-ui-lib';

type NewsSectionType = 'dailyDish' | 'debrief';
type ContentWatchSectionType = 'frequency' | 'contentPreference' | 'tags' | 'checkbox';

type ContentWatchIndicatorType = 'contentWatchFrequency' | 'contentWatchPreference' | 'contentWatchTags' | 'contentWatchCheckbox';

type SavedIndicatorSectionType = NewsSectionType | ContentWatchIndicatorType;

type SavedIndicatorsState = Record<SavedIndicatorSectionType, boolean>;

type TimeoutRefsType = Partial<Record<SavedIndicatorSectionType, NodeJS.Timeout>>;

type IndicatorMapType = Record<ContentWatchSectionType, ContentWatchIndicatorType>;

const Alerts: FC = () => {
    const {
        currentUser: { email },
        wWidth
    } = useContext(GlobalContext);
    const { addNotification } = useNotification();
    const isNewsEnabled = usePlatformFeatureFlag(NEWS_FEATURE_FLAG);
    const location = useLocation();
    const route = Routes.find(({ path }) => path === location.pathname) || Routes[0];

    const { isLoading, data, savedData, setData, saveWithData, isImmediateSaveLoading } = useContentWatch();

    const { fetchSuggestedTags, isLoading: isSuggestedTagsLoading, isError: hasSuggestedTagsError } = useSuggestedTags();

    const [savedIndicators, setSavedIndicators] = useState<SavedIndicatorsState>({
        dailyDish: false,
        debrief: false,
        contentWatchFrequency: false,
        contentWatchPreference: false,
        contentWatchTags: false,
        contentWatchCheckbox: false
    });

    const timeoutRefs = useRef<TimeoutRefsType>({});

    const [pendingSuggestedTagsIds, setPendingSuggestedTagsIds] = useState<number[] | null>(null);
    const hasRequestedSuggestedTagsRef = useRef(false);
    const hasEvaluatedInitialSeedRef = useRef(false);

    useEffect(() => {
        const currentTimeouts = timeoutRefs.current;
        return () => {
            Object.values(currentTimeouts).forEach((timeout) => {
                if (timeout) clearTimeout(timeout);
            });
        };
    }, []);

    /**
     * Show the auto-saved indicator for a specific section
     * If the screen is smaller than 1280px, show a notification instead of the inline indicator
     * since the inline indicator is not visible on smaller screens
     */
    const showSavedIndicator = useCallback(
        (section: SavedIndicatorSectionType) => {
            if (wWidth < 1280) {
                addNotification({
                    message: 'Your choice has been saved',
                    status: 'success'
                });
                return;
            }

            if (timeoutRefs.current[section]) {
                clearTimeout(timeoutRefs.current[section]);
            }

            setSavedIndicators((prev) => ({ ...prev, [section]: true }));

            timeoutRefs.current[section] = setTimeout(() => {
                setSavedIndicators((prev) => ({ ...prev, [section]: false }));
            }, 3000);
        },
        [wWidth, addNotification]
    );

    const handleNewsUpdate = useCallback(
        (section: NewsSectionType, updateData: Partial<iContentWatch>) => {
            if (isImmediateSaveLoading) {
                return;
            }

            const updatedData = new ContentWatch({
                model: {
                    ...data,
                    ...updateData
                }
            });

            setData(updateData);
            saveWithData(updatedData);
            showSavedIndicator(section);
        },
        [data, setData, saveWithData, showSavedIndicator, isImmediateSaveLoading]
    );

    const handleContentWatchUpdate = useCallback(
        (section: ContentWatchSectionType, updateData: Partial<iContentWatch>) => {
            if (isImmediateSaveLoading) {
                return;
            }

            const hasChanges = Object.keys(updateData).some((key) => {
                const dataKey = key as keyof iContentWatch;
                return data[dataKey] !== updateData[dataKey];
            });

            if (!hasChanges) {
                return;
            }

            const updatedData = new ContentWatch({
                model: {
                    ...data,
                    ...updateData
                }
            });

            setData(updateData);
            saveWithData(updatedData);

            const indicatorMap: IndicatorMapType = {
                frequency: 'contentWatchFrequency',
                contentPreference: 'contentWatchPreference',
                tags: 'contentWatchTags',
                checkbox: 'contentWatchCheckbox'
            };

            showSavedIndicator(indicatorMap[section]);
        },
        [data, setData, saveWithData, showSavedIndicator, isImmediateSaveLoading]
    );

    const getHasNoTopics = useCallback(
        ({ tagsIds, subscriptionPackagesIds }: iContentWatch) => tagsIds.length === 0 && subscriptionPackagesIds.length === 0,
        []
    );

    const requestSuggestedTags = useCallback(() => {
        if (hasRequestedSuggestedTagsRef.current) {
            return;
        }

        hasRequestedSuggestedTagsRef.current = true;

        fetchSuggestedTags().then((suggestedTags) => {
            if (suggestedTags.length === 0) return;

            setPendingSuggestedTagsIds(suggestedTags.map((tag) => tag.id));
        });
    }, [fetchSuggestedTags]);

    const handleSelectMyTopicsOnly = useCallback(() => {
        if (isImmediateSaveLoading) {
            return;
        }

        const isSwitchingToMyTopicsOnly = data.subscriptionType !== SubscriptionType.Related;

        handleContentWatchUpdate('contentPreference', { subscriptionType: SubscriptionType.Related });

        if (isSwitchingToMyTopicsOnly && getHasNoTopics(data)) {
            requestSuggestedTags();
        }
    }, [handleContentWatchUpdate, data, getHasNoTopics, requestSuggestedTags, isImmediateSaveLoading]);

    useEffect(() => {
        if (isLoading || hasEvaluatedInitialSeedRef.current) return;

        hasEvaluatedInitialSeedRef.current = true;

        if (savedData.subscriptionType === SubscriptionType.Related && getHasNoTopics(savedData)) {
            requestSuggestedTags();
        }
    }, [isLoading, savedData, getHasNoTopics, requestSuggestedTags]);

    useEffect(() => {
        if (pendingSuggestedTagsIds === null || isImmediateSaveLoading) return;

        setPendingSuggestedTagsIds(null);

        if (data.subscriptionType !== SubscriptionType.Related || !getHasNoTopics(data)) return;

        handleContentWatchUpdate('tags', { tagsIds: pendingSuggestedTagsIds });
    }, [pendingSuggestedTagsIds, isImmediateSaveLoading, data, getHasNoTopics, handleContentWatchUpdate]);

    return (
        <Page title={route!.title}>
            <div className="Alerts">
                <div className="Alerts__title">Alerts</div>
                <div className="Alerts__sub-title">
                    <b>Alerts keep you up to date on news and content as it's published!</b>
                    Your alerts will be sent to <i>{email}</i>.
                </div>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className="Alerts__content">
                        {isNewsEnabled && (
                            <>
                                <div className="Alerts__section">
                                    <div className="Alerts__section-header">
                                        <div className="Alerts__section-title">The Daily Dish: Food And Beverage News</div>
                                    </div>
                                    <div className="Alerts__section-content">
                                        Daily roundup of the F&B headlines you need to know
                                        <div className="Alerts__section-checkbox-wrapper">
                                            <Checkbox
                                                className="Alerts__section-checkbox"
                                                isChecked={data.isDailyDishEnabled}
                                                onChange={() => {
                                                    handleNewsUpdate('dailyDish', {
                                                        isDailyDishEnabled: !data.isDailyDishEnabled
                                                    });
                                                }}
                                            />
                                            {savedIndicators.dailyDish && (
                                                <div className="Alerts__saved-indicator">your choice has been saved</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="Alerts__section">
                                    <div className="Alerts__section-header">
                                        <div className="Alerts__section-title">Datassential Debrief: Insights In A Snap</div>
                                    </div>
                                    <div className="Alerts__section-content">
                                        Daily insights and trends distilled from our latest data
                                        <div className="Alerts__section-checkbox-wrapper">
                                            <Checkbox
                                                className="Alerts__section-checkbox"
                                                isChecked={data.isNewsArticleEnabled}
                                                onChange={() => {
                                                    handleNewsUpdate('debrief', {
                                                        isNewsArticleEnabled: !data.isNewsArticleEnabled
                                                    });
                                                }}
                                            />
                                            {savedIndicators.debrief && (
                                                <div className="Alerts__saved-indicator">your choice has been saved</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="Alerts__divider" />
                            </>
                        )}

                        <ContentWatchComponent
                            data={data}
                            setData={handleContentWatchUpdate}
                            onSelectMyTopicsOnly={handleSelectMyTopicsOnly}
                            suggestedTags={{
                                isLoading: isSuggestedTagsLoading,
                                isError: hasSuggestedTagsError
                            }}
                            showSavedIndicators={{
                                frequency: savedIndicators.contentWatchFrequency,
                                contentPreference: savedIndicators.contentWatchPreference,
                                tags: savedIndicators.contentWatchTags,
                                checkbox: savedIndicators.contentWatchCheckbox
                            }}
                        />
                    </div>
                )}
            </div>
        </Page>
    );
};

export default Alerts;
